import React, { Component } from "react";
import axios from "axios";
import PropTypes from "prop-types";

/**
 * BulkWhatsAppCampaign - React CLASS component
 * - Sends POST { to: string[], message: string } to /api/marketing/bulk-whatsapp
 * - Recipients: comma/newline/semicolon separated phone numbers (provider format, usually with country code)
 *
 * Usage:
 * <BulkWhatsAppCampaign apiBaseUrl="/api/marketing" />
 */
export class BulkWhatsAppCampaign extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recipientsText: "",
      message: "",
      sending: false,
      successMessage: "",
      errorMessage: "",
      validationErrors: [],
      previewOpen: false,
    };
  }

  parseRecipients = (text) => {
    if (!text) return [];
    const parts = text
      .split(/[,;\n\r]+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    return Array.from(new Set(parts));
  };

  // Basic phone check similar to SMS: WhatsApp usually requires international format (with country code)
  phoneIsValid = (phone) => {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) return false;
    const phoneRegex = /^[+\d\-\s().]{7,}$/;
    return phoneRegex.test(phone);
  };

  validate = () => {
    const errors = [];
    const to = this.parseRecipients(this.state.recipientsText);
    if (!to.length) errors.push("At least one recipient is required.");
    if (!this.state.message || !this.state.message.trim())
      errors.push("Message is required.");
    const invalid = to.filter((t) => !this.phoneIsValid(t));
    if (invalid.length)
      errors.push(
        `Invalid phone numbers detected: ${invalid.slice(0, 5).join(", ")}${
          invalid.length > 5 ? " (and more)" : ""
        }`
      );
    this.setState({ validationErrors: errors });
    return errors.length === 0;
  };

  handleSend = async (e) => {
    e && e.preventDefault();
    this.setState({ successMessage: "", errorMessage: "" });

    if (!this.validate()) return;

    const to = this.parseRecipients(this.state.recipientsText);
    const payload = { to, message: this.state.message };

    this.setState({ sending: true });
    try {
      const url = `${this.props.apiBaseUrl.replace(/\/$/, "")}/bulk-whatsapp`;
      const resp = await axios.post(url, payload, {
        timeout: this.props.timeout,
      });
      if (resp.status >= 200 && resp.status < 300) {
        this.setState({
          successMessage:
            resp.data?.message || "WhatsApp messages queued/sent successfully.",
          errorMessage: "",
        });
      } else {
        this.setState({ errorMessage: `Unexpected response: ${resp.status}` });
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data || err.message || String(err);
      this.setState({ errorMessage: `Error sending: ${msg}` });
    } finally {
      this.setState({ sending: false });
    }
  };

  handleChange = (field) => (e) => this.setState({ [field]: e.target.value });

  togglePreview = () => this.setState((s) => ({ previewOpen: !s.previewOpen }));

  renderValidation = () => {
    if (!this.state.validationErrors.length) return null;
    return (
      <div className="alert alert-warning" role="alert">
        <strong>Fix these before sending:</strong>
        <ul className="mb-0">
          {this.state.validationErrors.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
      </div>
    );
  };

  render() {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Bulk WhatsApp Campaign</h5>

          {this.state.successMessage && (
            <div className="alert alert-success">
              {this.state.successMessage}
            </div>
          )}
          {this.state.errorMessage && (
            <div className="alert alert-danger">{this.state.errorMessage}</div>
          )}

          {this.renderValidation()}

          <form onSubmit={this.handleSend}>
            <div className="mb-3">
              <label className="form-label">
                Recipients (comma, semicolon or newline separated)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={this.state.recipientsText}
                onChange={this.handleChange("recipientsText")}
                placeholder="+919XXXXXXXXX, +1-555-555-5555"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows={6}
                value={this.state.message}
                onChange={this.handleChange("message")}
              />
              <div className="form-text">
                WhatsApp can carry long messages — you can include simple markup
                supported by your provider (templates, variables etc.).
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={this.state.sending}
              >
                {this.state.sending ? "Sending..." : "Send WhatsApp"}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={this.togglePreview}
              >
                {this.state.previewOpen ? "Hide Preview" : "Preview"}
              </button>

              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() =>
                  this.setState({
                    recipientsText: "",
                    message: "",
                    validationErrors: [],
                    successMessage: "",
                    errorMessage: "",
                  })
                }
              >
                Clear
              </button>
            </div>
          </form>

          {this.state.previewOpen && (
            <div className="mt-3">
              <h6>Preview</h6>
              <div className="border p-3" style={{ minHeight: 120 }}>
                <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {this.state.message}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

BulkWhatsAppCampaign.propTypes = {
  apiBaseUrl: PropTypes.string,
  timeout: PropTypes.number,
};

BulkWhatsAppCampaign.defaultProps = {
  apiBaseUrl: "/api/marketing",
  timeout: 120000,
};
